import { Component, OnDestroy, OnInit } from '@angular/core';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { WireExportFileType } from '../../enums/wire-export-file-type.enum';
import { WireReportService } from '../WireReport/wire-report.service';
import { Subscription, filter } from 'rxjs';
import { WireService } from '../../services/wire.service';
import { WireModel } from '../../models/wire.model';
import { ClickStatModel, ReleaseStatsModel } from '../../models/release-stats.model';
import { jsPDF } from 'jspdf';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import moment from 'moment-timezone';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { download } from '../../../../Common/Misc/helpers/download/download.helper';


@Component({
    selector: 'agpr-wire-export-dialog',
    templateUrl: './wire-export-dialog.component.html',
    styleUrls: ['./wire-export-dialog.component.scss'],
    standalone: false
})
export class WireExportDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    selectedFileType: WireExportFileType = WireExportFileType.pdf;
    wire: WireModel;
    performanceReport: ReleaseStatsModel;
    exporting: boolean = false;
    cancelClicked: boolean = false;

    pagesProcessed: number = 0;
    totalPages: number = 1;
    timezone: string;
    worker: Worker;

    subscriptions: Subscription[] = [];
    readonly fileTypeEnum = WireExportFileType;
    readonly postingsPerPage = 6;

    generalStats = ['totalTraffic', 'totalPotentialVisibility', 'totalNumberOfPostings', 'totalLinkClicks', 'totalViews'];
    localization = {};

    constructor(
        public coreDialogService: CoreDialogService,
        public wireService: WireService,
        public wireReportService: WireReportService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService
    ) {
        super(coreDialogService, 'WireExport');
        this.subscriptions.push(
            this.wireService.currentResource$.subscribe(wire => this.wire = wire),
            this.wireReportService.getPerformanceReport().subscribe(report => {
                this.performanceReport = report;
                this.totalPages =  Math.ceil(this.performanceReport.postingStats.length / this.postingsPerPage) + 1;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(preferences => this.timezone = preferences.timeZone)
        );
    }

    ngOnInit() {
        if (typeof Worker !== 'undefined') {
            this.worker = new Worker(new URL('./wire-export.worker', import.meta.url), { type:  'module' });
        }
        // localizationService cannot be passed to web worker, and not possible to instatiate the service in the worker, so pass localization strings directly
        this.localization = {
            publicationDate: this.localizationService.transform('Amplify.wireReport.export.publicationDate') + ': ',
            exportDate: this.localizationService.transform('Amplify.wireReport.export.exportDate') + ': ',
            generalStatsLabel: [...Array(5).keys()].map(num => this.localizationService.transform(`Amplify.wireReport.general.${this.generalStats[num]}`)),
            topTargetClicks: this.localizationService.transform('Amplify.wireReport.click.topLinkClicks'),
            topSourceClicks: this.localizationService.transform('Amplify.wireReport.click.topClicksBySource'),
            clickRates: this.localizationService.transform('Amplify.wireReport.click.clickRates'),
            postingsHeader: this.localizationService.transform('Amplify.wireReport.postings.header'),
            screenshot: this.localizationService.transform('Amplify.wireReport.postings.screenshot'),
            date: this.localizationService.transform('Amplify.wireReport.postings.date'),
            url: this.localizationService.transform('Amplify.wireReport.postings.url'),
            alexaGlobal: this.localizationService.transform('Amplify.wireReport.postings.alexaTrafficGlobal.full'),
            mozDA: this.localizationService.transform('Amplify.wireReport.postings.mozDA'),
            postingTraffic: this.localizationService.transform('Amplify.wireReport.postings.postingTraffic'),
            potentialVisibility: this.localizationService.transform('Amplify.wireReport.postings.potentialVisibility')
        };
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    async export() {
        this.exporting = true;
        if (this.selectedFileType === WireExportFileType.pdf) {
            if (typeof Worker !== 'undefined') {
                this.worker.postMessage(
                    {
                        action: 'start',
                        performanceReport: this.performanceReport,
                        postingsPerPage: this.postingsPerPage,
                        totalPages: this.totalPages,
                        timezone: this.timezone,
                        creationDate: this.wire.createdDate,
                        generalStats: this.generalStats,
                        localization: this.localization
                    }
                );
                this.worker.onmessage = (e) => {
                    if (typeof e.data.state === 'number') {
                        this.pagesProcessed = e.data.state;
                    } else {
                        download.blob(
                            e.data,
                            `${this.wire.name.split(' ').join('_').substring(0, 80)}_${this.wire.id}_Performance_${moment().format('YYYYMMDD')}.pdf`
                        );
                        this.exportComplete();
                    }
                };
            } else {
                this.exporting = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Amplify.wireReport.export.failureSummary',
                    detail: 'Amplify.wireReport.export.noBrowserSupport'
                });
            }
        } else {
            await this.exportCSV();
            this.exportComplete();
        }
    }

    exportComplete() {
        this.exporting = false;
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Amplify.wireReport.export.successSummary',
            detail: this.localizationService.transform('Amplify.wireReport.export.successDetail')
        });
    }

    async exportCSV(): Promise<void> {
        await this.wireReportService.exportCSVReport();
    }

    closeDialog() {
        this.cancelClicked = true;
        this.worker.postMessage({action: 'cancel'});
        this.worker.terminate();
        super.closeDialog();
    }
}
